import { ReactNode } from 'react';
import { useTheme } from '@emotion/react';
import { Trans } from '@lingui/react/macro';
import Icon from 'src/components/Icon';
import { useDarkMode } from '../../../context/DarkModeContext';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { getProcessSteps, ProcessPhase } from '../OcifBulkUpload/util';

const CIRCLE_SIZE = 48;
const CONNECTOR_GAP = CIRCLE_SIZE / 2 + 8;

function usePhaseColors(): Record<ProcessPhase, string> {
  const theme = useTheme() as { primaryMain: string; infoMain: string; successMain: string };
  const { isDarkMode } = useDarkMode();
  return {
    // The theme's primary is a deep navy that vanishes on a dark background.
    prepare: isDarkMode ? '#7eb8d4' : theme.primaryMain,
    upload: theme.infoMain,
    submit: theme.successMain,
  };
}

function PhaseLegendItem({ color, children }: { color: string; children: ReactNode }) {
  return (
    <span className="flex items-center gap-2">
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
      {children}
    </span>
  );
}

export function ProcessDiagram() {
  const phaseColors = usePhaseColors();
  const isMobile = useIsMobile();
  const steps = getProcessSteps();

  return (
    <div
      className="rounded-lg px-4 py-8 mb-4"
      style={{ background: `linear-gradient(90deg, ${phaseColors.prepare}0d, ${phaseColors.upload}0d 50%, ${phaseColors.submit}0d)` }}
    >
      <div className={isMobile ? 'grid grid-cols-3 gap-y-6' : 'flex'}>
        {steps.map((step, i) => {
          const color = phaseColors[step.phase];
          const next = steps[i + 1];
          return (
            <div key={step.label} className="relative flex flex-col items-center flex-1 px-1">
              {next && !isMobile && (
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: CIRCLE_SIZE / 2 - 1,
                    left: `calc(50% + ${CONNECTOR_GAP}px)`,
                    right: `calc(-50% + ${CONNECTOR_GAP}px)`,
                    height: 2,
                    borderRadius: 1,
                    background: `linear-gradient(90deg, ${color}, ${phaseColors[next.phase]})`,
                    opacity: 0.6,
                  }}
                />
              )}
              <span
                className="flex items-center justify-center"
                style={{
                  width: CIRCLE_SIZE,
                  height: CIRCLE_SIZE,
                  borderRadius: '50%',
                  border: `2px solid ${color}`,
                  background: `${color}1f`,
                  color,
                  boxShadow: `0 4px 12px ${color}33`,
                }}
              >
                <Icon icon={step.icon} size={22} />
              </span>
              <span className="mt-3 small font-bold text-center" style={{ lineHeight: 1.3 }}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex justify-center flex-wrap gap-x-6 gap-y-2 mt-6 small text-muted">
        <PhaseLegendItem color={phaseColors.prepare}>
          <Trans>Prepare offline</Trans>
        </PhaseLegendItem>
        <PhaseLegendItem color={phaseColors.upload}>
          <Trans>Upload on this page</Trans>
        </PhaseLegendItem>
        <PhaseLegendItem color={phaseColors.submit}>
          <Trans>Submit the report</Trans>
        </PhaseLegendItem>
      </div>
    </div>
  );
}
